import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, MessageCircle, MessageCircleOff, Mail, MailX, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AuthedImage from '@/components/AuthedImage';
import ImageLightbox from '@/components/ImageLightbox';
import { formatInZone } from '@/lib/serverTime';
import { badgeStatus } from '@/lib/contactStatus';
import { timezoneForContact } from '@/lib/showTimezone';

/**
 * The contact row for Home, task: contact row redesign (2026-09-08), styling
 * pass. This is a visual pass on top of the row that already removed the
 * TradeSync templated send button and capture time (those live on the
 * detail page now, see EditContact.jsx): tinted action chips, a real dashed
 * "unavailable" treatment instead of opacity, and an in-card warning bar
 * instead of a bottom toast.
 *
 * Colors are inline arbitrary Tailwind values, not new global CSS variables:
 * these five hex pairs (light) plus their dark equivalents belong to this
 * one action row, not the shared design system, so they live next to the
 * one component that uses them. dark: variants are real, distinct values
 * (not the light color reused), tied to Tailwind's existing `dark: ["class"]`
 * config (index.css already has a full `.dark` block). Worth knowing:
 * nothing in this app currently ever adds the `.dark` class anywhere, so
 * this dark styling is correct and ready but dormant until something turns
 * dark mode on.
 */

function initialsOf(firstName, lastName) {
  const a = (firstName || '').trim()[0] || '';
  const b = (lastName || '').trim()[0] || '';
  return (a + b).toUpperCase() || '?';
}

/**
 * "Marklen Kennedy" -> "Marklen K", "Mike Terrell" -> "Mike T". Task: rep
 * badge date/time, 2026-09-17. A rep with only one name segment on file
 * (no last name) shows just that segment, no trailing space and no broken
 * initial, since there is nothing to abbreviate.
 */
function abbreviateRepName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}`;
}

/**
 * One of the two photo circles. Tapping a real photo opens it larger; a
 * missing photo does nothing, there is nothing to enlarge.
 *
 * `distinctWhenMissing` is only ever passed for the right (badge/card)
 * circle: the owner's own ask was to tell the two missing-photo cases apart
 * at a glance, not show identical initials on both sides.
 */
function PhotoCircle({ url, firstName, lastName, onOpen, label, distinctWhenMissing = false }) {
  if (url) {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(url); }}
        className="w-14 h-14 rounded-full overflow-hidden border border-border shrink-0"
        title={label}
      >
        <AuthedImage src={url} alt="" className="w-full h-full object-cover" fallbackClassName="w-14 h-14" />
      </button>
    );
  }
  return (
    <div
      className={`w-14 h-14 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 border ${
        distinctWhenMissing
          ? 'bg-amber-50 text-amber-700 border-amber-200 border-dashed dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800'
          : 'bg-secondary text-foreground/70 border-border'
      }`}
      title={label}
    >
      {initialsOf(firstName, lastName)}
    </div>
  );
}

/**
 * One tinted action chip: available uses its own color, one per action;
 * unavailable never uses opacity (unreadable, text dims against the
 * background with it), it switches to the muted surface, a dashed stronger
 * border, and the icon's own "off" variant, since the struck through icon
 * is what communicates "not possible", not a faded one.
 */
function ActionChip({ icon: Icon, offIcon: OffIcon, label, href, disabled, colors, onUnavailable }) {
  return (
    <a
      href={disabled ? undefined : href}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          onUnavailable();
        }
      }}
      className={`flex-1 flex flex-col items-center justify-center gap-1 py-[9px] rounded-md text-xs font-medium transition-colors ${
        disabled
          ? 'bg-muted text-muted-foreground border border-dashed border-muted-foreground/50'
          : `${colors.bg} ${colors.text} border border-transparent`
      }`}
    >
      {disabled ? <OffIcon className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      {label}
    </a>
  );
}

const CHIP_COLORS = {
  call: { bg: 'bg-[#E1F5EE] dark:bg-[#0F3229]', text: 'text-[#0F6E56] dark:text-[#5EEAC7]' },
  text: { bg: 'bg-[#E6F1FB] dark:bg-[#102A40]', text: 'text-[#185FA5] dark:text-[#7FB8EE]' },
  email: { bg: 'bg-[#EEEDFE] dark:bg-[#211C3D]', text: 'text-[#534AB7] dark:text-[#B7AEF0]' },
};

/**
 * The in-card warning, task: inline warning not a bottom toast (2026-09-08).
 * Replaces the action row entirely on this one card for 3 seconds, right
 * where the rep's thumb already is, then reverts on its own. No tap
 * required, no close button.
 */
function WarningBar({ icon: Icon, message }) {
  return (
    <div className="flex items-center gap-2 px-3 py-[9px] bg-destructive/10 text-destructive text-[13px] font-medium rounded-b-xl">
      <Icon className="w-4 h-4 shrink-0" />
      {message}
    </div>
  );
}

export default function ContactRow({ meeting, index = 0 }) {
  const [lightbox, setLightbox] = useState(null);
  const [warning, setWarning] = useState(null); // { icon, message } | null
  const warningTimeout = useRef(null);
  const hasEmail = !!meeting.email;
  const hasPhone = !!meeting.phone;
  const firstName = meeting.first_name || 'this contact';
  // Computed, not stored: offline, OCR cannot run at all, so a capture the
  // rep could not read in booth lighting saves with whatever exists, down
  // to nothing but photos. Deriving this from the current name rather than
  // a saved flag means it clears itself the moment a name arrives, OCR or
  // by hand, with nothing to explicitly unset. 'pending' (has a photo, not
  // read yet) and 'unreadable' (read, still nothing) both display as
  // "Unnamed contact"; only 'unreadable' gets the second line telling the
  // rep this one actually needs typing rather than more waiting.
  const status = badgeStatus(meeting);
  const isIncomplete = status !== 'named';

  // Task: rep badge date/time, 2026-09-17. captured_at, per the owner's own
  // call, not created_at: consistent with the "day I met them" field this
  // app already shows elsewhere (EditContact.jsx, the "Captured..." line
  // above for an unnamed contact), even though its time of day is a fixed
  // placeholder (always noon UTC, MetOnField.jsx's own dateToIso) for
  // anything captured through this app's own flow, real only for the
  // handful of contacts imported directly from Base44. Same
  // captured_at-else-created_at fallback CONTACT_ORDER and that existing
  // "Captured..." line already use, for the 28 imported contacts with no
  // captured_at at all.
  const capturedRaw = meeting.captured_at || meeting.created_at;
  const capturedTimezone = timezoneForContact(meeting);
  const capturedDisplay = capturedRaw
    ? {
        date: formatInZone(capturedRaw, capturedTimezone, 'EEE MM/dd/yy'),
        time: formatInZone(capturedRaw, capturedTimezone, 'h:mm a zzz'),
      }
    : null;

  useEffect(() => () => { if (warningTimeout.current) clearTimeout(warningTimeout.current); }, []);

  const showWarning = (icon, message) => {
    if (warningTimeout.current) clearTimeout(warningTimeout.current);
    setWarning({ icon, message });
    warningTimeout.current = setTimeout(() => setWarning(null), 3000);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.03, 0.3) }}>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <Link to={`/contacts/${meeting.id}/edit`} className="flex items-center gap-3 p-3 hover:bg-secondary/30 transition-colors">
          <PhotoCircle
            url={meeting.person_photo_url}
            firstName={meeting.first_name}
            lastName={meeting.last_name}
            onOpen={setLightbox}
            label="Photo of them"
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              {isIncomplete ? (
                <p className="font-medium text-muted-foreground text-base italic truncate">Unnamed contact</p>
              ) : (
                <p className="font-medium text-foreground text-base truncate">{meeting.first_name} {meeting.last_name}</p>
              )}
              {meeting.meeting_booked && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">Booked</span>
              )}
            </div>
            {isIncomplete ? (
              <>
                {/* Capture time and show, not company/title: this stands in
                    for the identity a real name would give, so a rep can
                    tell two unnamed rows apart rather than seeing the same
                    label twice. */}
                <p className="text-sm text-muted-foreground truncate">
                  Captured {formatInZone(capturedRaw, capturedTimezone, "EEE MMM d 'at' h:mm a zzz")}
                  {meeting.show_name ? ` · ${meeting.show_name}` : ''}
                </p>
                {status === 'unreadable' && (
                  <p className="text-[13px] text-amber-600 dark:text-amber-400 font-medium truncate">
                    Badge unreadable, needs details by hand
                  </p>
                )}
              </>
            ) : (
              <>
                {meeting.company && (
                  <p className="text-sm text-muted-foreground truncate">
                    {meeting.company}
                    {typeof meeting.company_location_count === 'number' && (
                      <span className="inline-flex items-center gap-0.5 ml-1.5">
                        <MapPin className="w-3 h-3 inline -mt-0.5" /> {meeting.company_location_count} location{meeting.company_location_count === 1 ? '' : 's'}
                      </span>
                    )}
                  </p>
                )}
                {meeting.title && <p className="text-[13px] text-muted-foreground truncate">{meeting.title}</p>}
              </>
            )}
          </div>

          {/* Which rep this contact is credited to, task: show the
              associated rep on Home, 2026-09-16, repositioned and extended
              with the capture date/time, 2026-09-17. Placed in the gap
              between the text column and the badge photo circle, since show
              silos do not exist (CLAUDE.md section 2, every rep already
              sees every contact from every rep on this same list), and that
              gap is exactly where the owner asked for it after seeing a
              real screenshot. Data was already there: scanned_by_name has
              ridden on every contact response since CONTACT_COLUMNS was
              written (worker/lib/contacts.ts); show_location and
              scanned_by_timezone were added there this same round,
              specifically to drive the date/time below, and are otherwise
              unused so far. Shown for every contact, including a rep's own
              captures, rather than hidden when it happens to be the viewer:
              a shared device account has no "own" captures to compare
              against, and keeping it unconditional means one row always
              means the same thing regardless of who is looking at it.

              self-start, not the parent row's own items-center: this is
              the one piece of the row that aligns to the top instead of
              vertically centering with the photo circles and text column,
              confirmed with the owner rather than left centered like
              everything else here. */}
          {meeting.scanned_by_name && (
            <div className="shrink-0 self-start flex flex-col items-end gap-1">
              <span className="text-[11px] font-medium text-muted-foreground bg-secondary/60 px-2 py-1 rounded-full whitespace-nowrap">
                {abbreviateRepName(meeting.scanned_by_name)}
              </span>
              {capturedDisplay && (
                <div className="text-right leading-tight">
                  <p className="text-[10px] text-muted-foreground/70 whitespace-nowrap">{capturedDisplay.date}</p>
                  <p className="text-[10px] text-muted-foreground/70 whitespace-nowrap">{capturedDisplay.time}</p>
                </div>
              )}
            </div>
          )}

          <PhotoCircle
            url={meeting.badge_image_url}
            firstName={meeting.first_name}
            lastName={meeting.last_name}
            onOpen={setLightbox}
            label="Badge photo"
            distinctWhenMissing
          />
        </Link>

        {warning ? (
          <WarningBar icon={warning.icon} message={warning.message} />
        ) : (
          <div className="border-t border-border grid grid-cols-3 gap-1.5 p-1.5">
            <ActionChip
              icon={Phone}
              offIcon={PhoneOff}
              label="Call"
              href={`tel:${meeting.phone}`}
              disabled={!hasPhone}
              colors={CHIP_COLORS.call}
              onUnavailable={() => showWarning(PhoneOff, `No cell number for ${firstName}`)}
            />
            <ActionChip
              icon={MessageCircle}
              offIcon={MessageCircleOff}
              label="Text"
              href={`sms:${meeting.phone}`}
              disabled={!hasPhone}
              colors={CHIP_COLORS.text}
              onUnavailable={() => showWarning(MessageCircleOff, `No cell number for ${firstName}`)}
            />
            <ActionChip
              icon={Mail}
              offIcon={MailX}
              label="Email"
              href={`mailto:${meeting.email}`}
              disabled={!hasEmail}
              colors={CHIP_COLORS.email}
              onUnavailable={() => showWarning(MailX, `No email address for ${firstName}`)}
            />
          </div>
        )}
      </div>

      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </motion.div>
  );
}
